import React, { useEffect, useRef, useState } from "react";
import { Tooltip, TooltipProps } from "antd";
import styles from "./OverflowTooltip.module.scss";

/**
 * Props for the OverflowTooltip component
 * @param text - The text to display in the tooltip and as the content
 * @param width - The width of the content element (default: 180px)
 * @param placement - The placement of the tooltip relative to the content
 *   Possible values:
 *   - 'top' (default)
 *   - 'topLeft'
 *   - 'topRight'
 *   - 'left'
 *   - 'leftTop'
 *   - 'leftBottom'
 *   - 'right'
 *   - 'rightTop'
 *   - 'rightBottom'
 *   - 'bottom'
 *   - 'bottomLeft'
 *   - 'bottomRight'
 * @param align - Custom alignment of the tooltip
 */
type Props = {
  text: string;
  width?: number;
  placement?: TooltipProps['placement'];
  align?: TooltipProps['align'];
};

function OverflowTooltip(props: Props) {
  const { text, width, placement = "top", align } = props;
  const textRef = useRef<HTMLDivElement>(null);
  const [isOverflow, setIsOverflow] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      const el = textRef.current;
      if (el) {
        setIsOverflow(el.scrollWidth > el.clientWidth);
      }
    };

    // Check overflow on mount and when the text changes
    checkOverflow();

    // Optional: Add a resize listener to detect layout changes
    window.addEventListener("resize", checkOverflow);
    return () => {
      window.removeEventListener("resize", checkOverflow);
    };
  }, [text]);

  return (
    <Tooltip
      title={
        isOverflow ? <div className={styles.tooltipText}>{text}</div> : ""
      }
      placement={placement}
      color="#FFFFFF"
      align={align}
    >
      <div
        ref={textRef}
        style={{
          width: width ? `${width}px` : "180px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          fontSize: 15
        }}
      >
        {text}
      </div>
    </Tooltip>
  );
}

export default OverflowTooltip;
